import { useQuery } from '@tanstack/react-query'

type GeocodedLocation = {
	lat: number
	lng: number
}

type CityToGeocode = {
	id: string
	name: string
	countryName: string
	radius: number
}

type GeocodedCity = {
	id: string
	name: string
	lat: number
	lng: number
	radius: number
}

export type UseGeocodeMultipleArgs = {
	cities: CityToGeocode[]
}

export type UseGeocodeMultipleReturns = {
	cities: GeocodedCity[]
	isLoading: boolean
	pendingCount: number
	errorCount: number
}

const NOMINATIM_API_URL = import.meta.env.VITE_NOMINATIM_API_URL || 'https://nominatim.openstreetmap.org'

// Nominatim requires at least 1 second between requests
const NOMINATIM_DELAY_MS = 1000

async function geocodeCity(cityName: string, countryName: string): Promise<GeocodedLocation | null> {
	const params = new URLSearchParams({
		city: cityName,
		country: countryName,
		format: 'json',
		limit: '1',
	})

	const response = await fetch(
		`${NOMINATIM_API_URL}/search?${params.toString()}`,
		{
			headers: {
				'User-Agent': 'JobillaTAS/1.0 (https://jobilla.com)',
			},
		},
	)

	if (!response.ok) {
		throw new Error('Geocoding failed')
	}

	const data = await response.json()

	if (data.length === 0) {
		return null
	}

	return {
		lat: parseFloat(data[0].lat),
		lng: parseFloat(data[0].lon),
	}
}

async function geocodeCitiesSerially(cities: CityToGeocode[]): Promise<Map<string, GeocodedLocation | null>> {
	const results = new Map<string, GeocodedLocation | null>()

	for (let i = 0; i < cities.length; i++) {
		const city = cities[i]

		// Wait before each request (except the first) to respect rate limits
		if (i > 0) {
			await new Promise((resolve) => setTimeout(resolve, NOMINATIM_DELAY_MS))
		}

		try {
			const location = await geocodeCity(city.name, city.countryName)
			results.set(city.id, location)
		} catch {
			results.set(city.id, null)
		}
	}

	return results
}

export function useGeocodeMultiple({ cities }: UseGeocodeMultipleArgs): UseGeocodeMultipleReturns {
	// Create a stable query key based on city IDs
	const cityIds = cities.map((c) => c.id).sort().join(',')

	const query = useQuery({
		queryKey: ['geocode-batch', cityIds],
		queryFn: () => geocodeCitiesSerially(cities),
		enabled: cities.length > 0,
		staleTime: 24 * 60 * 60 * 1000, // 24 hours
		gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days
	})

	const geocodedCities: GeocodedCity[] = []
	let errorCount = 0

	if (query.data) {
		cities.forEach((city) => {
			const location = query.data.get(city.id)
			if (location) {
				geocodedCities.push({
					id: city.id,
					name: city.name,
					lat: location.lat,
					lng: location.lng,
					radius: city.radius,
				})
			} else {
				errorCount++
			}
		})
	}

	const pendingCount = query.isLoading ? cities.length : 0

	return {
		cities: geocodedCities,
		isLoading: query.isLoading,
		pendingCount,
		errorCount,
	}
}
